// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { BrandModel, CategoryModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import { xSeoHandlerType } from "@synd/App/Handlers/XNG/Seo";
import { Draft } from "immer";

export interface xSeoSchemaModel<T> {
    isActive?: boolean;
    items: T;
}

export interface ProductCarouselSeoModel {
    name: string;
    id: string;
    type: string;
    products: ProductModel[];
    isFeatured?: boolean;
}

export type xSeoStateType = Readonly<{
    featuredCategories?: xSeoSchemaModel<CategoryModel[]>;
    featuredBrands?: xSeoSchemaModel<BrandModel[]>;
    productCarousels?: xSeoSchemaModel<ProductCarouselSeoModel[]>;
}>;

export const initialxSeo: xSeoStateType = {
    featuredCategories: {
        isActive: false,
        items: [],
    },
    featuredBrands: {
        isActive: false,
        items: [],
    },
    productCarousels: {
        items: [],
    },
};

const reducer = {
    "xSeo/update": (draft: Draft<xSeoStateType>, action: { data: xSeoHandlerType }) => {
        const { key, operation, value } = action.data;
        const entity = draft[key as keyof xSeoStateType] as any;

        if (!entity) return;

        switch (operation) {
            case "add":
                entity.items.push(...value);
                entity.isActive = true;
                break;

            case "delete": {
                const idsToRemove = new Set(value.map((item: any) => item.id));
                entity.items = entity.items.filter((item: any) => !idsToRemove.has(item.id));
                entity.isActive = entity.items.length > 0;
                break;
            }

            case "replace":
                entity.items = value;
                entity.isActive = false;
                break;

            default:
                entity.items.push(...value);
                entity.isActive = true;
        }
    },
};

const xSeo = createTypedReducerWithImmer(initialxSeo, reducer);

(reducers as any).xSeo = xSeo;

export type xSeoState = {
    xSeo: xSeoStateType;
} & ApplicationState;

type xSeoReducers = {
    [key: string]: typeof xSeo;
};

export type xSeoActions = Parameters<xSeoReducers[keyof xSeoReducers]>[1] | AnyAction;
